import { useState, useEffect, useRef, useCallback } from 'react';

interface VideoItem {
  id: string;
  title: string;
  youtubeId: string;
}

const videos: VideoItem[] = [
  {
    id: 'media-1',
    title: 'Bella siccome un angelo',
    youtubeId: 'ssYmI9n5ovc',
  },
  {
    id: 'media-2',
    title: 'Hai già vinta la causa',
    youtubeId: 'icC8RfKs15s',
  },
  {
    id: 'media-3',
    title: 'Mein Sehnen, Mein Wähnen',
    youtubeId: 'OhsX2oXkiZE',
  },
  {
    id: 'media-4',
    title: 'Mab, la reine des mensonges',
    youtubeId: 'ik3R4XMyrA8',
  },
  {
    id: 'media-5',
    title: 'Curt, She Called Him Curt',
    youtubeId: 'c03cWrYpsqU',
  },
  {
    id: 'media-6',
    title: "Come un'ape ne giorni d'aprile",
    youtubeId: 'kv8hDXzxl_M',
  },
];

function getThumbnailUrl(youtubeId: string): string {
  return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
}

function getEmbedUrl(youtubeId: string): string {
  return `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`;
}

export default function MediaContent() {
  const [activeVideo, setActiveVideo] = useState<VideoItem | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const openModal = useCallback((video: VideoItem) => {
    setActiveVideo(video);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setActiveVideo(null);
    document.body.style.overflow = '';
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeVideo) {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [activeVideo, closeModal]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.classList.add('reveal-fade-up');
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative bg-background-50 px-6 md:px-10 lg:px-16 py-16 md:py-24">
      <div ref={contentRef} className="max-w-6xl mx-auto">
        {/* Video Grid */}
        <div className="stagger-children grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {videos.map((video) => (
            <button
              key={video.id}
              type="button"
              onClick={() => openModal(video)}
              className="group relative bg-white border border-foreground-100 overflow-hidden cursor-pointer transition-all duration-500 hover:border-foreground-200 hover:shadow-sm text-left w-full"
              aria-label={`Play ${video.title}`}
            >
              {/* Thumbnail */}
              <div className="aspect-video bg-background-100 relative overflow-hidden">
                <img
                  src={getThumbnailUrl(video.youtubeId)}
                  alt={video.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Play overlay */}
                <div className="absolute inset-0 bg-background-950/0 group-hover:bg-background-950/20 transition-colors duration-500 flex items-center justify-center">
                  <span className="w-12 h-12 rounded-full bg-background-50/80 flex items-center justify-center group-hover:bg-background-50/95 group-hover:scale-110 transition-all duration-500 shadow-sm">
                    <i className="ri-play-fill text-foreground-700 text-lg ml-[2px] group-hover:ml-[3px] transition-all duration-300"></i>
                  </span>
                </div>
              </div>

              {/* Title */}
              <div className="p-4">
                <p className="text-foreground-800 text-sm font-heading group-hover:text-foreground-950 transition-colors duration-300">
                  {video.title}
                </p>
              </div>
            </button>
          ))}
        </div>

        {/* YouTube Channel CTA */}
        <div className="mt-14 md:mt-20 text-center">
          <a
            href="https://youtube.com/@keatonbrown5120?si=NbaGX6QERhflWBHe"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-6 py-3 bg-primary-500 text-foreground-50 rounded-full text-sm tracking-[0.12em] uppercase hover:bg-primary-600 transition-all duration-300 hover:scale-105 cursor-pointer"
          >
            <i className="ri-youtube-line text-lg"></i>
            View My YouTube Channel
          </a>
        </div>
      </div>

      {/* Video Modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
          role="dialog"
          aria-modal="true"
          aria-label={`Playing ${activeVideo.title}`}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-background-950/80 backdrop-blur-sm animate-[gentleFadeIn_0.3s_ease-out]"
            onClick={closeModal}
          />

          {/* Modal Content */}
          <div className="relative z-10 w-full max-w-4xl animate-[fadeScaleIn_0.4s_cubic-bezier(0.25,0.1,0.25,1)]">
            {/* Close Button */}
            <button
              type="button"
              onClick={closeModal}
              className="absolute -top-10 right-0 md:-top-12 md:-right-12 w-8 h-8 flex items-center justify-center text-foreground-50/80 hover:text-foreground-50 transition-colors duration-300 whitespace-nowrap"
              aria-label="Close video"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {/* Video Player */}
            <div className="aspect-video bg-background-950 rounded-lg overflow-hidden shadow-2xl">
              <iframe
                src={getEmbedUrl(activeVideo.youtubeId)}
                title={activeVideo.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>

            {/* Video Title */}
            <p className="text-foreground-50/90 text-sm font-heading mt-3 text-center md:text-left">
              {activeVideo.title}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}